import type { MouseEventHandler } from 'react';
import { X } from 'lucide-react';
import { Button, Card, Description } from '@heroui/react';
import type { ArrowRenderProps, TooltipRenderProps } from 'react-joyride';

/**
 * O Joyride entrega `onClick`; o Button do HeroUI usa `onPress`.
 * O handler do tour não depende do evento, então um clique sintético basta.
 */
function toPress(onClick: MouseEventHandler<HTMLElement>) {
  return () => onClick({ preventDefault() {}, stopPropagation() {} } as never);
}

/** Seta do popover no tom da Card; o Joyride gira conforme o lado do alvo. */
export function OnboardingTourArrow({ base, size }: ArrowRenderProps) {
  return (
    <div
      className="bg-surface"
      style={{
        width: base,
        height: size,
        clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
      }}
    />
  );
}

/** Tooltip do tutorial com Card/Button do HeroUI — o motor do tour continua o Joyride. */
export function OnboardingTourTooltip({
  backProps,
  closeProps,
  continuous,
  index,
  isLastStep,
  primaryProps,
  size,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  return (
    <div {...tooltipProps} className="w-[min(20rem,calc(100vw-2rem))]">
      <Card className="shadow-lg">
        <Card.Header className="flex-row items-start justify-between gap-3 pb-0">
          <Card.Title className="text-base font-semibold leading-snug">
            {step.title}
          </Card.Title>
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            aria-label={closeProps['aria-label'] || 'Fechar'}
            onPress={toPress(closeProps.onClick)}
          >
            <X size={16} />
          </Button>
        </Card.Header>
        <Card.Content className="pt-2">
          <Description>{step.content}</Description>
        </Card.Content>
        <Card.Footer className="flex items-center justify-between gap-2 pt-0">
          <span className="text-xs tabular-nums text-muted">
            {index + 1}/{size}
          </span>
          <div className="flex items-center gap-2">
            {index > 0 ? (
              <Button size="sm" variant="secondary" onPress={toPress(backProps.onClick)}>
                Voltar
              </Button>
            ) : null}
            {continuous ? (
              <Button size="sm" variant="primary" onPress={toPress(primaryProps.onClick)}>
                {isLastStep ? 'Entendi' : 'Próximo'}
              </Button>
            ) : null}
          </div>
        </Card.Footer>
      </Card>
    </div>
  );
}
